/**
 * nextOpenGymSession — pure "next session" math for the Open Gym public detail
 * page. Web twin of the mobile
 * cheervue-mobile-app/utils/programs/nextClassOccurrence.ts, extended with:
 * blackout awareness (explicit closed-dates set from the availability route's
 * unavailable_dates + gym_closings union), rolling past today's already-ended
 * slot, per-slot effective_date/end_date bounds, and a bounded forward
 * lookahead across closures.
 *
 * Deliberately clock-free: callers pass gym-local "now" parts (dateStr +
 * minutesOfDay), so this never reads the machine clock or timezone and stays
 * deterministic under any TZ. Date-only strings are never fed to `new Date()`
 * (UTC-midnight footgun) — parsing goes through parseDateLocal.
 */

import {
  parseDateLocal,
  formatDateOnly,
  timeToMinutes,
  getZonedDateParts,
} from '@/utils/formatters/dateUtils';
import { WEEKDAY_NAMES_SHORT } from '@/utils/constants/weekdays';

export interface OpenGymSessionSlot {
  day_of_week: number; // 0 = Sunday … 6 = Saturday (matches Date.getDay())
  start_time: string; // 'HH:mm' or 'HH:mm:ss'
  end_time: string; // 'HH:mm' or 'HH:mm:ss'
  sectionId: string; // owning section, so callers can attribute per-section spots
  /** Optional schedule-window bounds ('YYYY-MM-DD'); dates outside are skipped. */
  effective_date?: string | null;
  end_date?: string | null;
  /**
   * Owning section's location, for location-scoped closures. Matches the
   * availability route's closedDatesByLocation keying: the section's
   * location_id, with NO_LOCATION_KEY ('null') as the bucket for sections
   * without a location (same convention as DropInCalendar).
   */
  locationId?: string | null;
}

/** Gym-local "now", precomputed by the caller (e.g. via the gym's IANA tz). */
export interface GymNowParts {
  dateStr: string; // gym-local today, 'YYYY-MM-DD'
  minutesOfDay: number; // gym-local minutes since midnight (0–1439)
}

/**
 * Gym-local "now" for the given IANA timezone. Falls back to Eastern if the
 * timezone string is missing/invalid (Intl throws RangeError) — shared by the
 * public detail route (date-only usage) and the detail page (date + time).
 * Date-only strings are never fed to `new Date()` (UTC-midnight footgun).
 */
export function getGymNowParts(timeZone: string | null | undefined): GymNowParts {
  const toParts = (p: { year: number; month: number; day: number; hour: number; minute: number }): GymNowParts => ({
    dateStr: `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`,
    minutesOfDay: p.hour * 60 + p.minute,
  });
  const now = new Date();
  try {
    return toParts(getZonedDateParts(now, timeZone || 'America/New_York'));
  } catch {
    return toParts(getZonedDateParts(now, 'America/New_York'));
  }
}

export interface NextOpenGymSession {
  date: string; // 'YYYY-MM-DD'
  slot: OpenGymSessionSlot;
  sectionId: string;
}

/** How far forward to scan across closures before giving up (inclusive). */
export const NEXT_SESSION_LOOKAHEAD_DAYS = 60;

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * Soonest upcoming open-gym occurrence across weekly slots.
 *
 * - Today counts while a slot's end_time hasn't passed (end_time <= now rolls).
 * - Every date in `closedDates` is skipped entirely for ALL slots (global set:
 *   program-level unavailable_dates — or the full union in legacy callers).
 * - `closedDatesByLocation` scopes gym_closings per slot: a date closed at the
 *   slot's `locationId` key ('null' for no-location slots) skips only that
 *   slot, mirroring DropInCalendar. Org-wide (null-location) closings are
 *   already baked into every location's set by getEffectiveClosedDates.
 * - Same-day ties break on the earlier start time.
 * - Scans today + NEXT_SESSION_LOOKAHEAD_DAYS days, then returns null.
 */
export function nextOpenGymSession(
  slots: Array<Partial<OpenGymSessionSlot>> | null | undefined,
  closedDates: Set<string> | string[] | null | undefined,
  gymNow: GymNowParts,
  closedDatesByLocation?: Map<string, Set<string>> | null
): NextOpenGymSession | null {
  const valid = (slots || []).filter(
    (s): s is OpenGymSessionSlot =>
      !!s &&
      typeof s.day_of_week === 'number' &&
      s.day_of_week >= 0 &&
      s.day_of_week <= 6 &&
      !!s.start_time &&
      !!s.end_time &&
      !!s.sectionId
  );
  if (valid.length === 0) return null;

  const closedSet = closedDates instanceof Set ? closedDates : new Set(closedDates || []);
  const base = parseDateLocal(gymNow.dateStr);

  for (let offset = 0; offset <= NEXT_SESSION_LOOKAHEAD_DAYS; offset++) {
    // Noon construction sidesteps any DST-midnight edge while day math stays exact.
    const day = new Date(base.getFullYear(), base.getMonth(), base.getDate() + offset, 12);
    const dateStr = formatDateOnly(day);
    if (closedSet.has(dateStr)) continue;

    const dow = day.getDay();
    const candidates = valid.filter((s) => {
      if (s.day_of_week !== dow) return false;
      // 'YYYY-MM-DD' compares correctly as a string — no Date round-trip needed.
      if (s.effective_date && dateStr < s.effective_date) return false;
      if (s.end_date && dateStr > s.end_date) return false;
      // Location-scoped closure: only this slot's location key applies.
      if (closedDatesByLocation?.get(s.locationId ?? 'null')?.has(dateStr)) return false;
      // Today only counts while the slot hasn't ended yet.
      if (offset === 0 && timeToMinutes(s.end_time) <= gymNow.minutesOfDay) return false;
      return true;
    });
    if (candidates.length === 0) continue;

    const winner = candidates.sort(
      (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
    )[0];
    return { date: dateStr, slot: winner, sectionId: winner.sectionId };
  }

  return null;
}

/**
 * Day label for the "Next session" line:
 * - session is gym-local today → 'Tonight' when it starts at/after 5 PM, else 'Today'
 * - tomorrow → 'Tomorrow'
 * - otherwise → 'Sat, Jul 18' style (weekday abbrev, month abbrev, day — no year)
 *
 * Pure string/parts math — safe under any machine TZ.
 */
export function formatSessionDayLabel(
  todayStr: string,
  sessionDate: string,
  startTime: string
): string {
  if (sessionDate === todayStr) {
    return timeToMinutes(startTime) >= 17 * 60 ? 'Tonight' : 'Today';
  }

  const today = parseDateLocal(todayStr);
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, 12);
  if (sessionDate === formatDateOnly(tomorrow)) return 'Tomorrow';

  const session = parseDateLocal(sessionDate);
  return `${WEEKDAY_NAMES_SHORT[session.getDay()]}, ${
    MONTH_NAMES_SHORT[session.getMonth()]
  } ${session.getDate()}`;
}
